/**
 * Widget export utilities — PNG download, copy-to-clipboard, CSV export.
 *
 * Phase J of issue #90 v3 plan. Used by WidgetExportMenu in the widget
 * header. Pulls data from the rendered ECharts instance via the widget
 * card's DOM (no need to thread a ref through GenericMetricWidget).
 */
import * as echarts from 'echarts/core'

/** Find the ECharts instance attached to an EChartsForReact-rendered chart
 *  inside the given root element. Returns null if no chart is found. */
function findEchartsInstance(root: HTMLElement): echarts.ECharts | null {
  // echarts-for-react renders a div with `_echarts_instance_` attribute set.
  const node = root.querySelector('div[_echarts_instance_]') as HTMLElement | null
  if (!node) return null
  return (echarts as any).getInstanceByDom(node) as echarts.ECharts | null
}

/** Trigger a browser download of the given data URL with the given filename. */
function downloadDataURL(dataURL: string, filename: string) {
  const a = document.createElement('a')
  a.href = dataURL
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/** Sanitize a string for use as a filename. */
function safeFilename(s: string): string {
  return s.replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 80)
}

/**
 * Export the chart inside `root` as a PNG download. `title` becomes the
 * filename stem (with `.png` appended).
 */
export function exportWidgetPng(root: HTMLElement, title: string): boolean {
  const instance = findEchartsInstance(root)
  if (!instance) return false
  const dataURL = instance.getDataURL({
    type: 'png',
    pixelRatio: 2,
    backgroundColor: '#fff',
  })
  downloadDataURL(dataURL, `${safeFilename(title)}.png`)
  return true
}

/**
 * Copy the chart inside `root` to the clipboard as a PNG image.
 * Returns true on success, false if clipboard access failed or the chart
 * couldn't be found.
 */
export async function copyWidgetToClipboard(root: HTMLElement): Promise<boolean> {
  const instance = findEchartsInstance(root)
  if (!instance) return false
  const dataURL = instance.getDataURL({
    type: 'png',
    pixelRatio: 2,
    backgroundColor: '#fff',
  })
  try {
    const blob = await (await fetch(dataURL)).blob()
    // ClipboardItem is async-supported in modern Chromium / Safari / Firefox.
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
      return true
    }
  } catch (err) {
    console.error('Clipboard copy failed:', err)
  }
  return false
}

/**
 * Convert an array of row objects to a CSV string and trigger a download.
 * Auto-detects column headers from the first row (enriched with any
 * additional keys present on later rows).
 */
export function exportRowsAsCsv(rows: Array<Record<string, unknown>>, title: string): boolean {
  if (rows.length === 0) return false
  const headers = new Set<string>()
  for (const row of rows) {
    for (const k of Object.keys(row)) headers.add(k)
  }
  const cols = Array.from(headers)

  const escape = (v: unknown): string => {
    if (v === null || v === undefined) return ''
    const s = String(v)
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`
    }
    return s
  }

  const lines = [cols.join(',')]
  for (const row of rows) {
    lines.push(cols.map((c) => escape(row[c])).join(','))
  }
  const csv = lines.join('\n')
  const dataURL = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
  downloadDataURL(dataURL, `${safeFilename(title)}.csv`)
  return true
}
